<script lang="ts">
  import {
    getCapabilityReport,
    reportToText,
    type CapabilityReport,
  } from '../platform/capabilities';
  import { copyText } from './clipboard';

  let report = $state<CapabilityReport | null>(null);
  let error = $state<string | null>(null);
  let copied = $state<'idle' | 'ok' | 'fail'>('idle');

  $effect(() => {
    getCapabilityReport()
      .then((r) => (report = r))
      .catch((e: unknown) => (error = String(e)));
  });

  async function copy() {
    if (!report) return;
    copied = (await copyText(reportToText(report))) ? 'ok' : 'fail';
    setTimeout(() => (copied = 'idle'), 2500);
  }
</script>

<section class="panel" data-testid="capability-report">
  <div class="head">
    <h3>About this browser</h3>
    <button onclick={copy} disabled={!report} data-testid="copy-report">
      {copied === 'ok' ? 'Copied ✓' : copied === 'fail' ? 'Copy failed' : 'Copy report'}
    </button>
  </div>
  <p class="hint">
    What this browser supports. If something doesn't work, copy this report and send it to your
    instructor.
  </p>
  {#if error}
    <p class="bad">Could not build the report: {error}</p>
  {:else if !report}
    <p class="hint">Checking…</p>
  {:else}
    {#each report.sections as section (section.title)}
      <h4>{section.title}</h4>
      <table>
        <tbody>
          {#each section.items as item (item.label)}
            <tr data-status={item.status}>
              <td class="mark" aria-label={item.status}>
                {item.status === 'ok'
                  ? '✓'
                  : item.status === 'missing'
                    ? '✗'
                    : item.status === 'warn'
                      ? '!'
                      : '·'}
              </td>
              <td>{item.label}</td>
              <td class="value">{item.value}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/each}
  {/if}
</section>

<style>
  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
  h3 {
    margin: 0;
  }
  h4 {
    margin: 1rem 0 0.3rem;
    font-size: 0.95rem;
  }
  .hint {
    color: var(--muted);
    font-size: 0.9rem;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.9rem;
  }
  td {
    padding: 0.2rem 0.5rem;
    border-bottom: 1px solid var(--border);
    vertical-align: top;
  }
  .mark {
    width: 1.5rem;
    text-align: center;
    font-weight: 600;
  }
  .value {
    color: var(--muted);
    font-family: var(--mono);
    font-size: 0.85rem;
  }
  tr[data-status='ok'] .mark {
    color: var(--ok);
  }
  tr[data-status='warn'] .mark {
    color: var(--warn);
  }
  tr[data-status='missing'] .mark {
    color: var(--bad);
  }
  .bad {
    color: var(--bad);
  }
</style>
